import { onBeforeUnmount, ref } from "vue";

const MIN_COLUMN_WIDTH = 40;

/**
 * Shared column-width state and optional sort state for data-preview tables.
 * Fetching and applying the selected sort are intentionally owned by each
 * consumer because their APIs may have different sorting contracts.
 */
export function useResizableTable({
  columns,
  storageKey,
  enableSorting = true,
  initialSortKey = "",
  initialSortDirection = "ASC",
}) {
  const columnWidths = ref({});
  const sortKey = ref(initialSortKey);
  const sortDirection = ref(initialSortDirection);
  const resizing = ref(null);

  const persistWidths = () => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(storageKey, JSON.stringify(columnWidths.value));
  };

  const restoreColumnWidths = () => {
    if (typeof window === "undefined") return;
    try {
      const stored = JSON.parse(window.localStorage.getItem(storageKey) || "{}");
      if (stored && typeof stored === "object") columnWidths.value = stored;
    } catch {
      columnWidths.value = {};
    }
  };

  const widthFor = (column) =>
    Math.max(MIN_COLUMN_WIDTH, Number(columnWidths.value[column.key]) || column.width || 160);
  const columnStyle = (column) => ({ width: `${widthFor(column)}px` });
  const gridStyle = (gridColumns = columns) => ({
    width: `${gridColumns.reduce((total, column) => total + widthFor(column), 0)}px`,
  });

  const toggleSort = (key) => {
    if (!enableSorting) return;
    if (sortKey.value === key) {
      sortDirection.value = sortDirection.value === "ASC" ? "DESC" : "ASC";
    } else {
      sortKey.value = key;
      sortDirection.value = "ASC";
    }
  };

  const sortIcon = (key) => (sortKey.value !== key ? "↕" : sortDirection.value === "ASC" ? "↑" : "↓");
  const sortAriaValue = (column) =>
    sortKey.value !== column.key ? "none" : sortDirection.value === "ASC" ? "ascending" : "descending";
  const sortLabel = (column) => {
    const direction = sortKey.value === column.key && sortDirection.value === "ASC" ? "malejąco" : "rosnąco";
    return `Sortuj według: ${column.label}, ${direction}`;
  };

  const resizeColumn = (event) => {
    if (!resizing.value) return;
    columnWidths.value = {
      ...columnWidths.value,
      [resizing.value.key]: Math.max(
        MIN_COLUMN_WIDTH,
        resizing.value.startWidth + event.clientX - resizing.value.startX,
      ),
    };
  };

  const stopResize = () => {
    if (resizing.value) persistWidths();
    resizing.value = null;
    document.body.classList.remove("resizable-data-grid--resizing");
    window.removeEventListener("pointermove", resizeColumn);
  };

  const startResize = (event, column) => {
    resizing.value = { key: column.key, startX: event.clientX, startWidth: widthFor(column) };
    document.body.classList.add("resizable-data-grid--resizing");
    window.addEventListener("pointermove", resizeColumn);
    window.addEventListener("pointerup", stopResize, { once: true });
  };

  const resizeWithKeyboard = (column, amount) => {
    columnWidths.value = { ...columnWidths.value, [column.key]: Math.max(MIN_COLUMN_WIDTH, widthFor(column) + amount) };
    persistWidths();
  };

  onBeforeUnmount(() => {
    window.removeEventListener("pointermove", resizeColumn);
    window.removeEventListener("pointerup", stopResize);
    document.body.classList.remove("resizable-data-grid--resizing");
  });

  return {
    enableSorting,
    columnWidths,
    sortKey,
    sortDirection,
    columnStyle,
    gridStyle,
    toggleSort,
    sortIcon,
    sortAriaValue,
    sortLabel,
    startResize,
    resizeWithKeyboard,
    restoreColumnWidths,
  };
}
